import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CardTabs, Skeleton, SubPartnerAccount, withStyles } from 'vopay';
import { BodyText, UtilButton } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import useFetchPartnerPartners from '@/hooks/useApi/useFetchPartnerPartners';
import useFetchPartnerPartnerDetails from '@/hooks/useApi/useFetchPartnerPartnerDetails';
import PartnerCard from '@/components/Partners/PartnerManagement/PartnerCard';
import toPartnerCredentials from '@/components/Partners/PartnerManagement/toPartnerCredentials.helper';
import { PARTNER_MANAGEMENT_PATH } from '@/components/Partners/PartnerManagement/paths';
import * as partnerPageRoute from '@/components/Partners/PartnerManagement/partnerPage.route';
import PartnerAccountsTab from './PartnerAccountsTab';
import PartnerDetailsTab from './PartnerDetailsTab';
import PartnerEntitlementsTab from './PartnerEntitlementsTab';
import PartnerPermissionsTab from './PartnerPermissionsTab';
import PartnerPortalUsersTab from './PartnerPortalUsersTab';
import PartnerTransactionsTab from './PartnerTransactionsTab';
import styles from './PartnerDetailsPage.module.scss';

const cn = withStyles(styles);

/**
 * The full page of one partner account (spec 5.5 / 5.6), behind the drawer's View Full Details:
 * the same identity card as the drawer, then the tabs — Details, Permissions, Partner Entitlement,
 * Partner Portal Users, Accounts and Account Transactions. The Permissions tab is why the card here
 * has no View Permissions link, unlike the list's drawer. The list row (fetched here by ID, with
 * `ShowToken`) carries the name, status, dates and the partner account's own bearer, which the tabs
 * sign their reads with; the program terms and credentials come from the details request.
 */
export default function PartnerDetailsPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerDetailsPage' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { partnerAccountID, tab } = useParams<{ partnerAccountID: string; tab?: string }>();
  const history = useHistory();
  const toast = useToast();
  const { fetchPartnerPartners } = useFetchPartnerPartners();
  const { partnerDetails, fetchingPartnerDetails, fetchPartnerPartnerDetails, reset } = useFetchPartnerPartnerDetails();

  // The row for this page's partner account: undefined while the list request is out, null once it
  // has answered without the row (or failed). Held here rather than read from the hook so that a
  // list response for a partner account the user has since left cannot replace it.
  const [partner, setPartner] = useState<SubPartnerAccount | null>();
  const [hasDetailsFailed, setHasDetailsFailed] = useState(false);

  useEffect(() => {
    reset();
    setPartner(undefined);
    setHasDetailsFailed(false);

    // The route stays mounted as the user moves between partner pages (back / forward), so a
    // request for the previous partner can still answer here: its result must not land on this one.
    let isCurrentPartner = true;

    // SearchText also matches names, so the row is picked out of the response by ID.
    fetchPartnerPartners({ ShowToken: true, SearchText: partnerAccountID })
      .then((res) => {
        if (!isCurrentPartner) return;

        setPartner(res.Partners.find((candidate) => candidate.PartnerAccountID === partnerAccountID) ?? null);
      })
      .catch((error) => {
        if (!isCurrentPartner) return;

        // The hook has logged the cause; the page reports the partner account as unavailable.
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        setPartner(null);

        toast.neutral(
          t('loadError', { defaultValue: 'Could not load the partner account: {{errorMessage}}', errorMessage }),
        );
      });

    // The hook drops a superseded response itself, but its rejection still reaches this caller.
    fetchPartnerPartnerDetails(partnerAccountID).catch((error) => {
      if (!isCurrentPartner) return;

      // The hook has logged the cause. The card still renders what the row carries; the program
      // terms show as unavailable rather than loading forever.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setHasDetailsFailed(true);

      toast.neutral(
        t('detailsLoadError', {
          defaultValue: 'Could not load the partner details, so some fields are unavailable: {{errorMessage}}',
          errorMessage,
        }),
      );
    });

    return () => {
      isCurrentPartner = false;
    };
    // toast is deliberately left out: the provider recreates its value every render, so depending
    // on it would refetch on every toast.
  }, [partnerAccountID, fetchPartnerPartners, fetchPartnerPartnerDetails, reset, t]);

  // Undefined while loading, null when the request failed — the Details tab renders each differently.
  const detailsOrFailure = partnerDetails ?? (hasDetailsFailed ? null : undefined);

  // The tab is a path segment (/partner-management/:partnerAccountID/:tab), as on the account page,
  // so a reload or a link from elsewhere lands on the same one; a segment naming no tab lands on
  // Details. Replaced rather than pushed: Back leaves the page, not the tab.
  const activeTab: partnerPageRoute.PartnerPageTab = partnerPageRoute.isTab(tab) ? tab : partnerPageRoute.TABS[0];

  const setActiveTab = (nextTab: partnerPageRoute.PartnerPageTab) =>
    history.replace({ pathname: partnerPageRoute.getPath({ partnerAccountID, tab: nextTab }) });

  // A segment naming no tab is not left in the address bar: the URL says what the page shows.
  useEffect(() => {
    if (tab !== undefined && !partnerPageRoute.isTab(tab)) setActiveTab(activeTab);
  }, [tab]);

  // The list writes its filters with history.replace, so the entry behind this page is the list as
  // the user had it. Going back restores it; a page opened cold (a refresh or a shared link) has
  // nothing behind it, so it falls back to the bare list.
  const arrivedFromApp = useRef(history.action === 'PUSH');

  const handleBackClick = () => {
    if (arrivedFromApp.current) {
      history.goBack();

      return;
    }

    history.push(PARTNER_MANAGEMENT_PATH);
  };

  return (
    <div className={cn('page')} data-testid="partnerDetailsPage">
      <UtilButton size="medium" iconLeft="chevron_left" onClick={handleBackClick}>
        {tGlobal('Buttons.back')}
      </UtilButton>

      {partner === undefined && <Skeleton height={148} data-testid="partnerPageLoading" />}

      {partner === null && (
        <BodyText>{t('partnerUnavailable', { defaultValue: 'This partner account could not be loaded.' })}</BodyText>
      )}

      {partner && (
        <>
          <PartnerCard
            partner={partner}
            credentials={toPartnerCredentials(partnerDetails)}
            // Only an Active partner can have credentials to wait for.
            isLoadingCredentials={partner.IsActive && fetchingPartnerDetails}
            agreement={partnerDetails?.Agreement}
          />

          <CardTabs.Root
            value={activeTab}
            onValueChange={(value) => partnerPageRoute.isTab(value) && setActiveTab(value)}
            className={cn('tabsRoot')}
          >
            <CardTabs.List>
              <CardTabs.Trigger value="details">{t('Tabs.details', { defaultValue: 'Details' })}</CardTabs.Trigger>

              <CardTabs.Trigger value="permissions">
                {t('Tabs.permissions', { defaultValue: 'Permissions' })}
              </CardTabs.Trigger>

              <CardTabs.Trigger value="entitlements">
                {t('Tabs.partnerEntitlement', { defaultValue: 'Partner Entitlement' })}
              </CardTabs.Trigger>

              <CardTabs.Trigger value="users">
                {t('Tabs.partnerPortalUsers', { defaultValue: 'Partner Portal Users' })}
              </CardTabs.Trigger>

              <CardTabs.Trigger value="accounts">{t('Tabs.accounts', { defaultValue: 'Accounts' })}</CardTabs.Trigger>

              <CardTabs.Trigger value="transactions">
                {t('Tabs.accountTransactions', { defaultValue: 'Account Transactions' })}
              </CardTabs.Trigger>
            </CardTabs.List>

            <CardTabs.Content value="details">
              <PartnerDetailsTab partner={partner} details={detailsOrFailure} bearer={partner.Token ?? ''} />
            </CardTabs.Content>

            <CardTabs.Content value="permissions">
              <PartnerPermissionsTab partner={partner} />
            </CardTabs.Content>

            <CardTabs.Content value="entitlements">
              <PartnerEntitlementsTab partnerAccountID={partnerAccountID} />
            </CardTabs.Content>

            <CardTabs.Content value="users">
              <PartnerPortalUsersTab key={partnerAccountID} partnerAccountID={partnerAccountID} />
            </CardTabs.Content>

            <CardTabs.Content value="accounts">
              <PartnerAccountsTab partner={partner} partnerType={partnerDetails?.PartnerType ?? ''} />
            </CardTabs.Content>

            <CardTabs.Content value="transactions">
              <PartnerTransactionsTab partner={partner} />
            </CardTabs.Content>
          </CardTabs.Root>
        </>
      )}
    </div>
  );
}
